// Common types used throughout the frontend application

export enum ClothingType {
  TOP = "TOP",
  BOTTOM = "BOTTOM",
  SHOES = "SHOES",
  HAT = "HAT",
  BAG = "BAG",
  BELT = "BELT",
  OTHER = "OTHER",
}

export interface ClothingItem {
  type: ClothingType;
  brand: string;
  color: string;
  description: string;
  gender?: string;
}

export interface OutfitMetadata {
  eventType: string[];
  clothingItems: ClothingItem[];
  description: string;
  tags: string[];
}

export interface User {
  id: string;
  email: string;
  username?: string; // User's unique username (backend should provide this)
  displayName: string;
  profilePicture: string;
  isPrivate?: boolean;
  isVerified?: boolean; // User verification status from backend
  followerCount?: number; // Number of followers from backend
  followingCount?: number; // Number of following from backend
  createdAt: Date;
  lastLogin: Date;
  likedOutfits: string[]; // Array of outfit IDs
  uploadedOutfits: string[]; // Array of outfit IDs
  tryOnProfilePictureUrl?: string; // Private profile picture for virtual try-on
  tryOnPictureUploadedAt?: Date; // Timestamp when try-on picture was uploaded
  emailVerified?: boolean;
  gender?: string; // Gender preference (Men, Women, or empty)
  savedOutfits: string[]; // Array of saved outfit IDs
}

export interface Outfit {
  id: string;
  userId: string;
  title: string;
  description: string;
  imageUrl: string;
  thumbnailUrl?: string;
  eventType: string;
  tags: string[];
  clothingItems: ClothingItem[];
  createdAt: string | Date;
  updatedAt?: string | Date;
  ratings: {
    averageRating: number;
    totalRatings: number;
  };
  likes: number;
  aiAnalysis?: AIAnalysisResult;
  metadata?: OutfitMetadata;
  // User information (from backend)
  userDisplayName?: string;
  userName?: string;
  userProfilePicture?: string;
  // User information (nested object)
  user?: {
    displayName: string;
    profilePicture?: string;
  };
  // Vibe check reactions
  reactions?: { [emoji: string]: number };
  userReaction?: string | null;
}

export interface AIAnalysisResult {
  description: string;
  highlights: string[];
  suggestedTags: string[];
  detectedClothingItems: DetectedClothingItem[];
}

export interface DetectedClothingItem {
  type: ClothingType;
  confidence: number;
  suggestedBrands?: string[];
  colors: string[];
}

export interface Rating {
  outfitId: string;
  userId: string;
  rating: number; // 1-5
  timestamp: Date;
}

export interface RatingSummary {
  outfitId: string;
  averageRating: number;
  totalRatings: number;
  ratingDistribution: Record<number, number>; // Rating distribution from backend, ...}
  trendingScore: number;
}

export enum SortOption {
  TRENDING = "trending",
  NEWEST = "newest",
  HIGHEST_RATED = "highest_rated",
}

export interface SearchCriteria {
  query?: string;
  eventTypes?: string[];
  brands?: string[];
  clothingTypes?: string[];
  genderCategories?: string[];
  minRating?: number;
  sortBy?: SortOption;
  page?: number;
  pageSize?: number;
}

export interface SearchResults {
  outfits: Outfit[];
  totalResults: number;
  currentPage: number;
  totalPages: number;
  fromCache?: boolean;
}

export enum UploadStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export interface UploadResult {
  uploadId: string;
  status: UploadStatus;
  imageUrl: string;
}

export interface RatingResult {
  success: boolean;
  newAverageRating: number;
  totalRatings: number;
}

/**
 * Try-On Session type
 */
export interface TryOnSession {
  sessionId: string;
  userId: string;
  outfitId: string;
  status: 'processing' | 'completed' | 'failed';
  profilePictureUrl: string;
  outfitImageUrl: string;
  resultImageUrl?: string;
  errorMessage?: string;
  createdAt: Date;
  completedAt?: Date;
  ttl: number; // Auto-expire after 1 hour (Unix timestamp)
}

/**
 * Try-On Session status enum
 */
export enum TryOnSessionStatus {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * Try-On error codes
 */
export enum TryOnErrorCode {
  NO_PROFILE_PICTURE = 'NO_PROFILE_PICTURE',
  INVALID_IMAGE_FORMAT = 'INVALID_IMAGE_FORMAT',
  IMAGE_TOO_LARGE = 'IMAGE_TOO_LARGE',
  BEDROCK_SERVICE_ERROR = 'BEDROCK_SERVICE_ERROR',
  PROCESSING_TIMEOUT = 'PROCESSING_TIMEOUT',
  OUTFIT_NOT_FOUND = 'OUTFIT_NOT_FOUND',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
}

// Re-export ad-related types
export * from './ads';

// Re-export follow-related types
export * from './follow';

// Re-export user-related types
export * from './user';

// Re-export social & community types
export * from './social';

// Re-export gamification types
export * from './gamification';

// Re-export affiliate types
export * from './affiliate';

// Re-export fashion show reel types
export * from './fashion-show';

// Re-export notification types
export * from './notification';

// Re-export blog types
export * from './blog';
